/** Page kinds define merge, card creation, and navigation behavior. Turso maintains retrieval indexes over page columns. */

/** How a page's content is folded from its mutation log. */
export type PageMergePolicy = 'crdtMerge' | 'wholeReplace'
/** Where this page's flashcards come from. */
export type PageCardBirthPolicy = 'documentParse' | 'adoptionMint' | 'none'
/** What a sidebar tree walk does when it reaches this page. */
export type PageNavPolicy = 'walk' | 'skip' | 'stop'

/** General page search includes notes and language pages. */
export type PageSearchPolicy = 'noteFtsEmbedding' | 'none'

export type PageKindPolicy = {
  search: PageSearchPolicy
  merge: PageMergePolicy
  cardBirth: PageCardBirthPolicy
  nav: PageNavPolicy
}

/**
 * Enum codes for `pages.kind`. Codes are permanent: never reuse or renumber.
 */
export const pageKindCodes = {
  note: 0,
  language: 1,
  lexiconEntry: 2,
  vocabulary: 3,
  grammar: 4,
  databaseRow: 6,
  // A member's personal root (db-crdt-sync.spec.md :uc[root-pages-have-ulid-ids]): only the server creates it.
  personalRoot: 7,
} as const

export type PageKind = keyof typeof pageKindCodes

export const PAGE_KIND_POLICY: Record<PageKind, PageKindPolicy> = {
  // Holds no content of its own; the sidebar walks it to list the member's private pages.
  personalRoot: {
    search: 'none',
    merge: 'crdtMerge',
    cardBirth: 'none',
    nav: 'walk',
  },
  databaseRow: {
    search: 'noteFtsEmbedding',
    merge: 'crdtMerge',
    cardBirth: 'none',
    nav: 'skip',
  },
  note: {
    search: 'noteFtsEmbedding',
    merge: 'crdtMerge',
    cardBirth: 'documentParse',
    nav: 'walk',
  },
  language: {
    search: 'noteFtsEmbedding',
    merge: 'crdtMerge',
    cardBirth: 'documentParse',
    nav: 'walk',
  },
  lexiconEntry: {
    search: 'none',
    merge: 'crdtMerge',
    cardBirth: 'adoptionMint',
    nav: 'skip',
  },
  // System subpage: a MARKER, not a container. Entries are their own rows
  // parented to it; nothing is stored inside its content (Core 3).
  grammar: {
    search: 'none',
    merge: 'crdtMerge',
    cardBirth: 'none',
    nav: 'walk',
  },
  vocabulary: {
    search: 'none',
    merge: 'crdtMerge',
    cardBirth: 'none',
    nav: 'stop',
  },
}

export const PAGE_KINDS = Object.keys(PAGE_KIND_POLICY) as PageKind[]

export function isPageKind(value: unknown): value is PageKind {
  return typeof value === 'string' && value in PAGE_KIND_POLICY
}

/** Policy for a kind. Unknown kinds fall back to the note policy. */
export function pageKindPolicy(kind: unknown): PageKindPolicy {
  return isPageKind(kind) ? PAGE_KIND_POLICY[kind] : PAGE_KIND_POLICY.note
}

/** The single source of truth for "does this content replace instead of merge?" */
export function isWholeReplaceKind(kind: unknown): boolean {
  return pageKindPolicy(kind).merge === 'wholeReplace'
}

/** Kinds a nav tree walk must not render at all. */
export function navSkippedKinds(): PageKind[] {
  return PAGE_KINDS.filter((kind) => PAGE_KIND_POLICY[kind].nav === 'skip')
}

/** Kinds a nav tree walk renders but never descends into. */
export function navStopKinds(): PageKind[] {
  return PAGE_KINDS.filter((kind) => PAGE_KIND_POLICY[kind].nav === 'stop')
}

/** General page retrieval is kind-gated; index maintenance belongs to Turso. */
export function embeddedKinds(): PageKind[] {
  return PAGE_KINDS.filter(kind => PAGE_KIND_POLICY[kind].search === 'noteFtsEmbedding')
}
